import type { Permissions } from './usePermissions';

export type RouteAccessPredicate = (p: Permissions) => boolean;

export type ShellRouteAccessRule = {
  /**
   * Path prefix (no trailing slash). Longer prefixes are evaluated first
   * so `/settings/offboarding` wins over `/settings`.
   */
  prefix: string;
  when: RouteAccessPredicate;
  /** Short product name for the access-denied screen. */
  featureLabel: string;
};

function normalizePathname(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1);
  return pathname;
}

function pathMatchesPrefix(pathname: string, prefix: string): boolean {
  if (prefix === '/') return pathname === '/' || pathname === '';
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * §10.3 — Central RBAC map for in-shell routes (`AppLayout` + `<Outlet />`).
 * Align with `Sidebar` + `Topbar`: if a nav item is hidden for a role, the URL must not open it either.
 * Any path not covered here is allowed for every org member (incl. viewer) — same as nav "show: true".
 */
export const SHELL_ROUTE_ACCESS_RULES: ShellRouteAccessRule[] = [
  {
    prefix: '/settings',
    when: (p) => p.isOwner || p.isAdmin,
    featureLabel: 'Settings',
  },
  { prefix: '/team', when: (p) => p.canViewTeam, featureLabel: 'Team' },
  { prefix: '/reports', when: (p) => p.canViewReports, featureLabel: 'Reports' },
  { prefix: '/bulk', when: (p) => p.canViewReports, featureLabel: 'Bulk process' },
  // Orchestrator removed from UI (Phase 5) — redirects to /smart-match
  { prefix: '/workflows', when: (p) => p.canViewReports, featureLabel: 'Workflows' },
  { prefix: '/scheduler', when: (p) => p.canViewReports, featureLabel: 'Scheduler' },
  {
    prefix: '/emails',
    when: (p) => p.canSendEmail || p.canViewEmailActivity,
    featureLabel: 'Email',
  },
  {
    prefix: '/candidates',
    when: (p) => p.canViewAllCandidates || p.canCreateCandidate,
    featureLabel: 'Candidates',
  },
  {
    prefix: '/clients',
    when: (p) => p.canManageClients || p.isJuniorRecruiter,
    featureLabel: 'Clients',
  },
  {
    prefix: '/jobs',
    when: (p) => p.canManageJobs || p.isJuniorRecruiter,
    featureLabel: 'Jobs',
  },
  {
    prefix: '/requisitions',
    when: (p) => p.canManageClients || p.isJuniorRecruiter,
    featureLabel: 'Requisitions',
  },
];

const SORTED_SHELL_RULES = [...SHELL_ROUTE_ACCESS_RULES].sort(
  (a, b) => b.prefix.length - a.prefix.length,
);

export function evaluateShellRouteAccess(
  pathname: string,
  perms: Permissions,
): { ok: true } | { ok: false; featureLabel: string } {
  const path = normalizePathname(pathname);
  for (const rule of SORTED_SHELL_RULES) {
    if (pathMatchesPrefix(path, rule.prefix)) {
      return rule.when(perms) ? { ok: true } : { ok: false, featureLabel: rule.featureLabel };
    }
  }
  return { ok: true };
}

/** Full-viewport routes rendered outside `AppLayout` but still under `OrgGuard`. */
export type StandaloneProtectedRoute = {
  path: string;
  when: RouteAccessPredicate;
  featureLabel: string;
};

export const ORG_STANDALONE_ROUTE_ACCESS: StandaloneProtectedRoute[] = [
  {
    path: '/clients/new',
    when: (p) => p.canManageClients || p.isJuniorRecruiter,
    featureLabel: 'New client',
  },
  {
    path: '/settings/offboarding',
    when: (p) => p.canManageTeam,
    featureLabel: 'Tenant exit',
  },
];

export function evaluateStandaloneRouteAccess(
  pathname: string,
  perms: Permissions,
): { ok: true } | { ok: false; featureLabel: string } {
  const path = normalizePathname(pathname);
  for (const def of ORG_STANDALONE_ROUTE_ACCESS) {
    if (path === def.path || path.startsWith(`${def.path}/`)) {
      return def.when(perms) ? { ok: true } : { ok: false, featureLabel: def.featureLabel };
    }
  }
  return { ok: true };
}
